import React, { useState, useEffect } from "react";
import {
    Button,
    ButtonGroup,
    Card,
    CardHeader,
    CardBody,
    Input,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
    Spinner
} from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { apiURL } from '../../config';
import { FaTrash } from 'react-icons/fa';


const CustomTable = ({ columns, data }) => {
    return (
        <Table responsive>
            <thead>
                <tr>
                    {columns.map((column, index) => (
                        <th key={index}>{column}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {columns.map((column, colIndex) => (
                            <td key={colIndex}>
                                {column.toLowerCase() === "actions"
                                    ? row.actions
                                    : row[column.toLowerCase().replace(/ /g, "")]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const Classes = () => {
    const token = localStorage.getItem("token");

    const [searchQuery, setSearchQuery] = useState("");
    const [classes, setClasses] = useState([]);
    const [classToDelete, setClassToDelete] = useState(null);
    const [classDeleteModalOpen, setClassDeleteModalOpen] = useState(false);
    const [classToView, setClassToView] = useState(null);
    const [classViewModalOpen, setClassViewModalOpen] = useState(false);
    const [classToEdit, setClassToEdit] = useState(null);
    const [classEditModalOpen, setClassEditModalOpen] = useState(false);
    const [editClassName, setEditClassName] = useState("");
    const [editCourse, setEditCourse] = useState("");
    const [editInstructor, setEditInstructor] = useState("");
    const [allCourses, setAllCourses] = useState([]);
    const [allTeachers, setAllTeachers] = useState([]);
    const [className, setClassName] = useState("");
    const [course, setCourse] = useState("");
    const [instructor, setInstructor] = useState("");
    const [classModalOpen, setClassModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchClasses();
        fetchCourses();
        fetchTeachers();
    }, []);

    const fetchClasses = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${apiURL}/superadmin/classes`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            setClasses(response.data || []);
        } catch (error) {
            toast.error("Error fetching classes. Refresh the page");
            console.error("Error fetching classes:", error);
            setClasses([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCourses = async () => {
        try {
            const response = await axios.get(
                `${apiURL}/superadmin/courseName`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            setAllCourses(response.data?.courses || []);
        } catch (error) {
            console.error("Error fetching courses:", error);
            setAllCourses([]);
        }
    };

    const fetchTeachers = async () => {
        try {
            const response = await axios.get(
                `${apiURL}/superadmin/getTeachersName`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            setAllTeachers(response.data?.teachers || []);
        } catch (error) {
            console.error("Error fetching teachers:", error);
            setAllTeachers([]);
        }
    };

    const handleViewClass = (cls) => {
        setClassToView(cls);
        setClassViewModalOpen(true);
    };

    const toggleClassViewModal = () => {
        setClassToView(null);
        setClassViewModalOpen(!classViewModalOpen);
    };

    const handleEditClass = (cls) => {
        setClassToEdit(cls);
        setEditClassName(cls?.className || '-');
        setEditCourse(cls.course?._id || '-');
        setEditInstructor(cls?.instructor?._id || '-');
        setClassEditModalOpen(true);
    };

    const toggleClassEditModal = () => {
        setClassToEdit(null);
        setClassEditModalOpen(!classEditModalOpen);
    };

    const handleUpdateClass = async (e) => {
        e.preventDefault();
        try {
            await axios.put(
                `${apiURL}/superadmin/classes/${classToEdit._id}`,
                {
                    className: editClassName,
                    course: editCourse,
                    instructor: editInstructor,
                }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            fetchClasses();
            toggleClassEditModal();
            toast.success("Class updated successfully!");

        } catch (error) {
            toast.error("Error updating class. Try again later");
            console.error("Error updating class:", error);
        }
    };

    const handleDeleteClass = (cls) => {
        setClassToDelete(cls);
        setClassDeleteModalOpen(true);
    };

    const confirmDeleteClass = async () => {
        try {
            await axios.delete(
                `${apiURL}/superadmin/classes/${classToDelete._id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            fetchClasses();
            setClassDeleteModalOpen(false);
            toast.success("Class deleted successfully!");

        } catch (error) {
            toast.error("Error deleting class. Try Again Later");
            console.error("Error deleting class:", error);
        }
    };

    const toggleClassDeleteModal = () => {
        setClassToDelete(null);
        setClassDeleteModalOpen(!classDeleteModalOpen);
    };

    const handleAddClass = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${apiURL}/superadmin/classes`, {
                className: className,
                course: course,
                instructor: instructor,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            fetchClasses();
            setClassName("");
            setCourse("");
            setInstructor("");
            toggleClassModal();
            toast.success("Class added successfully!");

        } catch (error) {
            toast.error("Error adding new class. Try Again Later");
            console.error("Error adding class:", error);
        }
    };

    const toggleClassModal = () => {
        setClassModalOpen(!classModalOpen);
    };

    const filteredClasses = classes ? classes.filter(
        (cls) =>
            cls.className.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cls.course?.courseName?.toLowerCase().includes(searchQuery.toLowerCase())
    ) : [];

    const columns = [
        "Class Name",
        "Course Name",
        "Instructor",
        "Actions",
    ];

    return (
        <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Classes Management</h3>
                <Input
                    type="text"
                    placeholder="Search by Class or Course Name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ maxWidth: "300px" }}
                />
                <Button color="primary" size="sm" onClick={toggleClassModal}>
                    Add Class
                </Button>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <Spinner />
                ) : (
                    <CustomTable
                        columns={columns}
                        data={filteredClasses ? filteredClasses.map((cls) => ({
                            classname: cls.className,
                            coursename: cls.course?.courseName || 'N/A',
                            instructor: cls.instructor?.name || 'N/A',
                            actions: (
                                <ButtonGroup size="sm">
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => handleDeleteClass(cls)}
                                    >
                                        <FaTrash />
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleEditClass(cls)}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleViewClass(cls)}
                                    >
                                        View
                                    </Button>
                                </ButtonGroup>
                            ),
                        })) : []}
                    />
                )}
            </CardBody>

            {/* View Class Modal */}
            <Modal isOpen={classViewModalOpen} toggle={toggleClassViewModal} size="lg">
                <ModalHeader toggle={toggleClassViewModal}>
                    Class Details
                </ModalHeader>
                <ModalBody>
                    {classToView && (
                        <div>
                            <p>
                                <strong>Class Name:</strong> {classToView.className}
                            </p>
                            <p>
                                <strong>Course Name:</strong> {classToView.course?.courseName || 'N/A'}
                            </p>
                            <p>
                                <strong>Course Code:</strong> {classToView.course?.courseCode || 'N/A'}
                            </p>
                            <p>
                                <strong>Instructor:</strong> {classToView.instructor?.name || 'N/A'}
                            </p>
                            <p>
                                <strong>Subjects:</strong>
                            </p>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Subject Name</th>
                                        <th>Staff</th>
                                        <th>Passing Marks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classToView.course?.subjects?.map((sub, index) => (
                                        <tr key={index}>
                                            <td>{sub.subject?.name || 'N/A'}</td>
                                            <td>{sub.teacher?.name || 'N/A'}</td>
                                            <td>{sub.passingMarks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleClassViewModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Edit Class Modal */}
            <Modal isOpen={classEditModalOpen} toggle={toggleClassEditModal}>
                <form onSubmit={handleUpdateClass}>
                    <ModalHeader toggle={toggleClassEditModal}>
                        Edit Class
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="editClassName">Class Name</Label>
                            <Input
                                type="text"
                                id="editClassName"
                                placeholder="Enter Class Name"
                                value={editClassName}
                                onChange={(e) => setEditClassName(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="editCourse">Course</Label>
                            <Input
                                type="select"
                                id="editCourse"
                                value={editCourse}
                                onChange={(e) => setEditCourse(e.target.value)}
                                required
                            >
                                <option value="">Select a course</option>
                                {allCourses.map((course) => (
                                    <option key={course._id} value={course._id}>
                                        {course.courseName}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="editInstructor">Instructor</Label>
                            <Input
                                type="select"
                                id="editInstructor"
                                value={editInstructor}
                                onChange={(e) => setEditInstructor(e.target.value)}
                                required
                            >
                                <option value="">Select an instructor</option>
                                {allTeachers.map((teacher) => (
                                    <option key={teacher?._id || '-'} value={teacher?._id || '-'}>
                                        {teacher?.name || '-'}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">
                            Save Changes
                        </Button>
                        <Button color="secondary" onClick={toggleClassEditModal}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>

            {/* Delete Class Modal */}
            <Modal
                isOpen={classDeleteModalOpen}
                toggle={toggleClassDeleteModal}
            >
                <ModalHeader toggle={toggleClassDeleteModal}>
                    Delete Class
                </ModalHeader>
                <ModalBody>
                    {classToDelete ? (
                        <p>
                            Are you sure you want to delete the class:{" "}
                            {classToDelete.className}?
                        </p>
                    ) : (
                        <p>No class selected.</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={confirmDeleteClass}>
                        Delete
                    </Button>
                    <Button color="secondary" onClick={toggleClassDeleteModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Add Class Modal */}
            <Modal isOpen={classModalOpen} toggle={toggleClassModal}>
                <form onSubmit={handleAddClass}>
                    <ModalHeader toggle={toggleClassModal}>Add Class</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="className">Class Name</Label>
                            <Input
                                type="text"
                                id="className"
                                placeholder="Enter Class Name"
                                value={className}
                                onChange={(e) => setClassName(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="course">Course</Label>
                            <Input
                                type="select"
                                id="course"
                                value={course}
                                onChange={(e) => setCourse(e.target.value)}
                                required
                            >
                                <option value="">Select a course</option>
                                {allCourses.map((course) => (
                                    <option key={course._id} value={course._id}>
                                        {course.courseName}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="instructor">Instructor</Label>
                            <Input
                                type="select"
                                id="instructor"
                                value={instructor}
                                onChange={(e) => setInstructor(e.target.value)}
                                required
                            >
                                <option value="">Select an instructor</option>
                                {allTeachers.map((teacher) => (
                                    <option key={teacher?._id || '-'} value={teacher?._id || '-'}>
                                        {teacher?.name || '-'}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">
                            Add Class
                        </Button>
                        <Button color="secondary" onClick={toggleClassModal}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>
        </Card>
    );
};

export default Classes;
